import { LoadingUI, WithLoadingProgress } from "@/components/PageLoader";
import { useAuthenticatedAssetUrl } from "@/components/document-viewer/useAuthenticatedAssetUrl";
import { Logo } from "@/components/top-bar/Logo";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Building2, ChevronRight, Loader2 } from "lucide-react";
import { Suspense, lazy, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { useProjectStore } from "@/stores/projectStore";
import type { ProjectListItem } from "@/types/project";

const Viewer = lazy(() => import("./Viewer"));

interface ViewerWithProgressProps {
  projectId: string | null;
}

function ViewerWithProgress({ projectId }: ViewerWithProgressProps) {
  return (
    <WithLoadingProgress>
      <ViewerLoader projectId={projectId} />
    </WithLoadingProgress>
  );
}

function ViewerLoader({ projectId }: ViewerWithProgressProps) {
  return (
    <Suspense fallback={<LoadingUI />}>
      <ViewerContent projectId={projectId} />
    </Suspense>
  );
}

function ViewerContent({ projectId }: ViewerWithProgressProps) {
  return (
    <>
      <LoadingUI />
      <Viewer projectId={projectId} />
    </>
  );
}

interface ProjectSelectionCardProps {
  project: ProjectListItem;
  onSelectProject: (projectId: string) => void;
}

function ProjectSelectionCard({
  project,
  onSelectProject,
}: ProjectSelectionCardProps) {
  const projectIconAssetId = project.projectIconAsset?.id ?? null;
  const { url: projectIconUrl, loading: projectIconLoading } =
    useAuthenticatedAssetUrl(projectIconAssetId);

  return (
    <Card
      className="group border-slate-200/80 bg-white/88 shadow-[0_18px_45px_rgba(15,23,42,0.08)] backdrop-blur transition-transform duration-200 hover:-translate-y-1"
    >
      <CardHeader className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl border border-border/70 bg-white text-foreground shadow-sm">
            {projectIconLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : projectIconUrl ? (
              <img
                src={projectIconUrl}
                alt={`Icône du projet ${project.name}`}
                className="h-full w-full object-contain p-1"
              />
            ) : (
              <Building2 className="h-6 w-6" />
            )}
          </div>
          <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
            {project.parcels.length} parcelle
            {project.parcels.length > 1 ? "s" : ""}
          </span>
        </div>
        <div className="space-y-2">
          <CardTitle className="text-xl text-foreground">{project.name}</CardTitle>
          {project.description?.trim() ? (
            <CardDescription className="line-clamp-3 text-sm leading-6 text-muted-foreground">
              {project.description}
            </CardDescription>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Button
          type="button"
          className="h-auto w-full justify-between rounded-xl px-4 py-6 text-sm font-medium"
          onClick={() => onSelectProject(project.id)}
        >
          Ouvrir ce projet
          <ChevronRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

function ProjectSelectionScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projects = useProjectStore((state) => state.projects);
  const sortedProjects = [...projects].sort(
    (projectA, projectB) =>
      new Date(projectA.createdAt).getTime() -
      new Date(projectB.createdAt).getTime(),
  );

  const handleSelectProject = (projectId: string) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("projectId", projectId);
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(85,44,240,0.18),_transparent_38%),linear-gradient(180deg,_#f8fafc_0%,_#e2e8f0_100%)] px-6 py-10">
      <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-6xl flex-col justify-center">
        <div className="mb-8 max-w-2xl self-center text-center">
          <div className="mb-6 flex justify-center">
            <Logo />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Choisissez le projet à ouvrir
          </h1>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,360px))] justify-center gap-4">
          {sortedProjects.map((project) => (
            <ProjectSelectionCard
              key={project.id}
              project={project}
              onSelectProject={handleSelectProject}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function ProjectSelectionError() {
  const fetchProjects = useProjectStore((state) => state.fetchProjects);
  const projectsError = useProjectStore((state) => state.projectsError);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
      <Card className="w-full max-w-xl border-red-200 bg-white">
        <CardHeader>
          <CardTitle>Impossible de charger les projets</CardTitle>
          <CardDescription>
            {projectsError || "Une erreur inattendue est survenue."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button type="button" onClick={() => void fetchProjects()}>
            Réessayer
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default function ViewerRoute() {
  const [searchParams] = useSearchParams();
  const projects = useProjectStore((state) => state.projects);
  const projectsLoading = useProjectStore((state) => state.projectsLoading);
  const projectsInitialized = useProjectStore(
    (state) => state.projectsInitialized,
  );
  const projectsError = useProjectStore((state) => state.projectsError);
  const fetchProjects = useProjectStore((state) => state.fetchProjects);

  useEffect(() => {
    if (!projectsInitialized && !projectsLoading) {
      void fetchProjects();
    }
  }, [fetchProjects, projectsInitialized, projectsLoading]);

  const selectedProjectId = searchParams.get("projectId");
  const hasMultipleProjects = projects.length > 1;
  const selectedProject =
    selectedProjectId != null
      ? projects.find((project) => project.id === selectedProjectId) ?? null
      : null;

  if ((!projectsInitialized || projectsLoading) && projects.length === 0) {
    return <LoadingUI />;
  }

  if (projectsError && projects.length === 0) {
    return <ProjectSelectionError />;
  }

  if (hasMultipleProjects && !selectedProject) {
    return <ProjectSelectionScreen />;
  }

  const projectIdToLoad = selectedProject?.id ?? projects[0]?.id ?? null;

  return <ViewerWithProgress projectId={projectIdToLoad} />;
}
